<template>
  <div class="stories-management">
    <div class="header">
      <h2>上岸故事管理</h2>
      <div class="actions">
        <el-button type="primary" @click="handleAdd">
          <el-icon><Plus /></el-icon>
          新增故事
        </el-button>
        <el-button @click="fetchList">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 统计卡片 -->
    <el-row :gutter="20" class="stats-row">
      <el-col :span="4">
        <el-card class="stat-card">
          <div class="stat-value">{{ stats.total }}</div>
          <div class="stat-label">总故事数</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="stat-card approved">
          <div class="stat-value">{{ stats.approved }}</div>
          <div class="stat-label">已发布</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="stat-card pending">
          <div class="stat-value">{{ stats.pending }}</div>
          <div class="stat-label">待审核</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="stat-card featured">
          <div class="stat-value">{{ stats.featured }}</div>
          <div class="stat-label">精选故事</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="stat-card debt">
          <div class="stat-value">{{ formatMoney(stats.totalDebt) }}</div>
          <div class="stat-label">总债务金额</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="stat-card resolved">
          <div class="stat-value">{{ formatMoney(stats.totalResolved) }}</div>
          <div class="stat-label">已化解金额</div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 搜索和筛选 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="searchForm">
        <el-form-item label="关键词">
          <el-input v-model="searchForm.keyword" placeholder="标题/用户名/内容" clearable style="width: 200px" />
        </el-form-item>
        <el-form-item label="审核状态">
          <el-select v-model="searchForm.status" placeholder="全部状态" clearable style="width: 120px">
            <el-option label="全部" value="all" />
            <el-option label="待审核" value="pending" />
            <el-option label="已通过" value="approved" />
            <el-option label="已拒绝" value="rejected" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button @click="resetSearch">
            <el-icon><Refresh /></el-icon>
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 批量操作 -->
    <el-card class="batch-actions" v-if="selectedIds.length > 0">
      <span>已选择 {{ selectedIds.length }} 条记录</span>
      <el-button type="success" size="small" @click="handleBatchAction('approve')">批量通过</el-button>
      <el-button type="warning" size="small" @click="handleBatchAction('reject')">批量拒绝</el-button>
      <el-button type="primary" size="small" @click="handleBatchAction('feature')">批量精选</el-button>
      <el-button size="small" @click="handleBatchAction('unfeature')">取消精选</el-button>
      <el-button type="danger" size="small" @click="handleBatchAction('delete')">批量删除</el-button>
    </el-card>

    <!-- 故事列表 -->
    <el-card>
      <el-table 
        :data="dataList" 
        v-loading="loading" 
        stripe 
        border
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="50" />
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="title" label="标题" min-width="200">
          <template #default="{ row }">
            <div class="title-cell">
              <el-tag v-if="row.isFeatured" type="danger" size="small" class="featured-tag">精选</el-tag>
              <span>{{ row.title }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="userName" label="作者" width="100">
          <template #default="{ row }">
            {{ row.isAnonymous ? '匿名用户' : (row.userName || '平台官方') }}
          </template>
        </el-table-column>
        <el-table-column prop="debtAmount" label="债务金额" width="120">
          <template #default="{ row }">
            <span class="debt-amount">{{ formatMoney(row.debtAmount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="resolvedAmount" label="已化解" width="120">
          <template #default="{ row }">
            <span class="resolved-amount">{{ formatMoney(row.resolvedAmount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="resolvedDays" label="用时(天)" width="90" />
        <el-table-column prop="views" label="浏览" width="80" />
        <el-table-column prop="likes" label="点赞" width="80" />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="160">
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="280" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">
              查看
            </el-button>
            <el-button type="warning" size="small" @click="handleEdit(row)">
              编辑
            </el-button>
            <el-button 
              v-if="row.status === 'pending'" 
              type="success" 
              size="small" 
              @click="handleAudit(row, 'approved')"
            >
              通过
            </el-button>
            <el-button 
              v-if="row.status === 'pending'" 
              type="danger" 
              size="small" 
              @click="handleAudit(row, 'rejected')"
            >
              拒绝
            </el-button>
            <el-button 
              v-if="row.status === 'approved'"
              :type="row.isFeatured ? 'info' : 'primary'" 
              size="small" 
              @click="handleFeatured(row)"
            >
              {{ row.isFeatured ? '取消精选' : '设为精选' }}
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 新增/编辑对话框 -->
    <el-dialog 
      v-model="dialogVisible" 
      :title="dialogType === 'add' ? '新增故事' : '编辑故事'"
      width="700px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入故事标题" maxlength="100" show-word-limit />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="作者名称" prop="userName">
              <el-input v-model="form.userName" placeholder="请输入作者名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="匿名发布">
              <el-switch v-model="form.isAnonymous" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="债务金额" prop="debtAmount">
              <el-input-number v-model="form.debtAmount" :min="0" :precision="2" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="已化解金额">
              <el-input-number v-model="form.resolvedAmount" :min="0" :precision="2" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="用时(天)">
              <el-input-number v-model="form.resolvedDays" :min="0" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="故事内容" prop="content">
          <el-input 
            v-model="form.content" 
            type="textarea" 
            :rows="8" 
            placeholder="请输入故事内容"
            maxlength="2000"
            show-word-limit
          />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="设为精选">
              <el-switch v-model="form.isFeatured" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="审核状态">
              <el-select v-model="form.status" style="width: 100%">
                <el-option label="待审核" value="pending" />
                <el-option label="已通过" value="approved" />
                <el-option label="已拒绝" value="rejected" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">
          确定
        </el-button>
      </template>
    </el-dialog>

    <!-- 查看详情对话框 -->
    <el-dialog v-model="viewDialogVisible" title="故事详情" width="700px">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="ID">{{ viewData.id }}</el-descriptions-item>
        <el-descriptions-item label="标题">{{ viewData.title }}</el-descriptions-item>
        <el-descriptions-item label="作者">
          {{ viewData.isAnonymous ? '匿名用户' : (viewData.userName || '平台官方') }}
        </el-descriptions-item>
        <el-descriptions-item label="用户ID">{{ viewData.userId }}</el-descriptions-item>
        <el-descriptions-item label="债务金额">{{ formatMoney(viewData.debtAmount) }}</el-descriptions-item>
        <el-descriptions-item label="已化解">{{ formatMoney(viewData.resolvedAmount) }}</el-descriptions-item>
        <el-descriptions-item label="用时">{{ viewData.resolvedDays }} 天</el-descriptions-item>
        <el-descriptions-item label="浏览量">{{ viewData.views }}</el-descriptions-item>
        <el-descriptions-item label="点赞数">{{ viewData.likes }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag :type="getStatusType(viewData.status)">{{ getStatusText(viewData.status) }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="精选">
          <el-tag :type="viewData.isFeatured ? 'danger' : 'info'">
            {{ viewData.isFeatured ? '是' : '否' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDate(viewData.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="故事内容" :span="2">
          <div class="story-content">{{ viewData.content }}</div>
        </el-descriptions-item>
      </el-descriptions>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh, Search } from '@element-plus/icons-vue'
import { 
  getStoriesList, 
  getStoryDetail, 
  createStory, 
  updateStory, 
  deleteStory,
  auditStory,
  setFeatured,
  batchAction,
  getStoryStats
} from '@/api/modules/stories'

// 数据
const loading = ref(false)
const submitLoading = ref(false)
const dataList = ref([])
const selectedIds = ref([])
const dialogVisible = ref(false)
const viewDialogVisible = ref(false)
const dialogType = ref('add')
const formRef = ref(null)
const viewData = ref({})

const searchForm = reactive({
  keyword: '',
  status: 'all'
})

const pagination = reactive({
  page: 1,
  pageSize: 10,
  total: 0
})

const stats = reactive({
  total: 0,
  approved: 0,
  pending: 0,
  rejected: 0,
  featured: 0,
  totalDebt: 0,
  totalResolved: 0
})

const form = reactive({
  id: null,
  title: '',
  userName: '平台官方',
  content: '',
  debtAmount: 0,
  resolvedAmount: 0,
  resolvedDays: 0,
  isFeatured: false,
  isAnonymous: false,
  status: 'approved'
})

const rules = {
  title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
  content: [{ required: true, message: '请输入内容', trigger: 'blur' }],
  debtAmount: [{ required: true, message: '请输入债务金额', trigger: 'blur' }]
}

// 方法
const fetchList = async () => {
  loading.value = true
  try {
    const res = await getStoriesList({
      page: pagination.page,
      pageSize: pagination.pageSize,
      status: searchForm.status,
      keyword: searchForm.keyword
    })
    if (res.code === 200) {
      dataList.value = res.data.list
      pagination.total = res.data.pagination.total
    }
  } catch (error) {
    ElMessage.error('获取列表失败')
  } finally {
    loading.value = false
  }
}

const fetchStats = async () => {
  try {
    const res = await getStoryStats()
    if (res.code === 200) {
      Object.assign(stats, res.data)
    }
  } catch (error) {
    console.error('获取统计失败')
  }
}

const handleSearch = () => {
  pagination.page = 1
  fetchList()
}

const resetSearch = () => {
  searchForm.keyword = ''
  searchForm.status = 'all'
  handleSearch()
}

const handleSelectionChange = (selection) => {
  selectedIds.value = selection.map(item => item.id)
}

const handleAdd = () => {
  dialogType.value = 'add'
  Object.assign(form, {
    id: null,
    title: '',
    userName: '平台官方',
    content: '',
    debtAmount: 0,
    resolvedAmount: 0,
    resolvedDays: 0,
    isFeatured: false,
    isAnonymous: false,
    status: 'approved'
  })
  dialogVisible.value = true
}

const handleEdit = async (row) => {
  dialogType.value = 'edit'
  try {
    const res = await getStoryDetail(row.id)
    if (res.code === 200) {
      Object.assign(form, res.data)
      dialogVisible.value = true
    }
  } catch (error) {
    ElMessage.error('获取详情失败')
  }
}

const handleView = async (row) => {
  try {
    const res = await getStoryDetail(row.id)
    if (res.code === 200) {
      viewData.value = res.data
      viewDialogVisible.value = true
    }
  } catch (error) {
    ElMessage.error('获取详情失败')
  }
}

const handleSubmit = async () => {
  const valid = await formRef.value.validate().catch(() => false)
  if (!valid) return

  submitLoading.value = true
  try {
    let res
    if (dialogType.value === 'add') {
      res = await createStory(form)
    } else {
      res = await updateStory(form.id, form)
    }
    if (res.code === 200) {
      ElMessage.success(dialogType.value === 'add' ? '创建成功' : '更新成功')
      dialogVisible.value = false
      fetchList()
      fetchStats()
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (error) {
    ElMessage.error('操作失败')
  } finally {
    submitLoading.value = false
  }
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm('确定要删除这个故事吗？', '提示', {
      type: 'warning'
    })
    const res = await deleteStory(row.id)
    if (res.code === 200) {
      ElMessage.success('删除成功')
      fetchList()
      fetchStats()
    } else {
      ElMessage.error(res.message || '删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('删除失败')
    }
  }
}

const handleAudit = async (row, status) => {
  const actionText = status === 'approved' ? '通过' : '拒绝'
  try {
    await ElMessageBox.confirm(`确定要${actionText}这个故事吗？`, '提示', {
      type: 'warning'
    })
    const res = await auditStory(row.id, status)
    if (res.code === 200) {
      ElMessage.success(`审核${actionText}成功`)
      fetchList()
      fetchStats()
    } else {
      ElMessage.error(res.message || '审核失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('审核失败')
    }
  }
}

const handleFeatured = async (row) => {
  const newFeatured = !row.isFeatured
  try {
    const res = await setFeatured(row.id, newFeatured)
    if (res.code === 200) {
      ElMessage.success(newFeatured ? '已设为精选' : '已取消精选')
      fetchList()
      fetchStats()
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (error) {
    ElMessage.error('操作失败')
  }
}

const handleBatchAction = async (action) => {
  const actionTexts = {
    approve: '批量通过',
    reject: '批量拒绝',
    feature: '批量精选',
    unfeature: '批量取消精选',
    delete: '批量删除'
  }
  try {
    await ElMessageBox.confirm(`确定要${actionTexts[action]} ${selectedIds.value.length} 个故事吗？`, '提示', {
      type: 'warning'
    })
    const res = await batchAction(selectedIds.value, action)
    if (res.code === 200) {
      ElMessage.success(res.message)
      selectedIds.value = []
      fetchList()
      fetchStats()
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('操作失败')
    }
  }
}

const handleSizeChange = (size) => {
  pagination.pageSize = size
  fetchList()
}

const handlePageChange = (page) => {
  pagination.page = page
  fetchList()
}

const getStatusType = (status) => {
  const map = {
    pending: 'warning',
    approved: 'success',
    rejected: 'danger'
  }
  return map[status] || 'info'
}

const getStatusText = (status) => {
  const map = {
    pending: '待审核',
    approved: '已通过',
    rejected: '已拒绝'
  }
  return map[status] || status
}

const formatMoney = (value) => {
  if (!value) return '0元'
  if (value >= 10000) {
    return (value / 10000).toFixed(1) + '万'
  }
  return value.toLocaleString() + '元'
}

const formatDate = (date) => {
  if (!date) return '-'
  return new Date(date).toLocaleString('zh-CN')
}

// 初始化
onMounted(() => {
  fetchList()
  fetchStats()
})
</script>

<style scoped>
.stories-management {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  font-size: 20px;
  color: #ffffff;
}

.actions {
  display: flex;
  gap: 10px;
}

.stats-row {
  margin-bottom: 20px;
}

.stat-card {
  text-align: center;
  padding: 10px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #409eff;
}

.stat-label {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.stat-card.approved .stat-value { color: #67c23a; }
.stat-card.pending .stat-value { color: #e6a23c; }
.stat-card.featured .stat-value { color: #f56c6c; }
.stat-card.debt .stat-value { color: #909399; }
.stat-card.resolved .stat-value { color: #67c23a; }

.filter-card {
  margin-bottom: 20px;
}

.batch-actions {
  margin-bottom: 10px;
  background: #f0f9eb;
}

.batch-actions span {
  margin-right: 20px;
  color: #67c23a;
}

.title-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.featured-tag {
  flex-shrink: 0;
}

.debt-amount {
  color: #909399;
}

.resolved-amount {
  color: #67c23a;
  font-weight: bold;
}

.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.story-content {
  white-space: pre-wrap;
  line-height: 1.8;
  max-height: 300px;
  overflow-y: auto;
}
</style>
